const CONSENT_KEY = 'portfolio_consent_v1';
const VISITOR_KEY = 'portfolio_visitor_id';
const SESSION_KEY = 'portfolio_session_id';
export const CONSENT_VERSION = '1.0';

export const ANALYTICS_BASE_URL =
  process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:8867';

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function storeConsent(consent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

export function clearConsent() {
  localStorage.removeItem(CONSENT_KEY);
}

export function getOrCreateVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = createId();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

export function getOrCreateSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = createId();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmTerm: params.get('utm_term'),
    utmContent: params.get('utm_content'),
  };
}

function getConnectionInfo() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return {};
  return {
    connectionType: connection.effectiveType || connection.type || null,
    connectionDownlink: connection.downlink ?? null,
  };
}

export function collectClientDetails() {
  const utm = getUtmParams();
  const connection = getConnectionInfo();
  return {
    pagePath: window.location.pathname,
    pageTitle: document.title,
    pageUrl: window.location.href,
    referrer: document.referrer || null,
    ...utm,
    language: navigator.language || null,
    languages: navigator.languages ? Array.from(navigator.languages) : null,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    colorDepth: window.screen?.colorDepth,
    pixelRatio: window.devicePixelRatio,
    timezoneOffset: new Date().getTimezoneOffset(),
    timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    doNotTrack: navigator.doNotTrack || window.doNotTrack || null,
    ...connection,
  };
}

async function postJson(path, payload) {
  const response = await fetch(`${ANALYTICS_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  return response;
}

export async function sendConsentDecision({ analyticsAllowed }) {
  const allowed = Boolean(analyticsAllowed);
  const visitorId = allowed ? getOrCreateVisitorId() : createId();
  const sessionId = allowed ? getOrCreateSessionId() : createId();
  const consent = {
    analytics: allowed,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  storeConsent(consent);

  if (!allowed) {
    localStorage.removeItem(VISITOR_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  }

  try {
    await postJson('/api/v1/consent', {
      visitorId,
      sessionId,
      analyticsAllowed: consent.analytics,
      consentVersion: CONSENT_VERSION,
      pageUrl: window.location.href,
    });
  } catch (error) {
    console.warn('Failed to persist consent audit record', error);
  }

  return consent;
}

export async function trackPageView() {
  const consent = getStoredConsent();
  if (!consent?.analytics) return;

  const payload = {
    eventType: 'pageview',
    sessionId: getOrCreateSessionId(),
    visitorId: getOrCreateVisitorId(),
    consentAnalytics: true,
    consentVersion: consent.version || CONSENT_VERSION,
    ...collectClientDetails(),
  };

  try {
    await postJson('/api/v1/collect', payload);
  } catch (error) {
    console.warn('Analytics collect failed', error);
  }
}

export async function fetchInsightsSummary(adminToken) {
  const response = await fetch(`${ANALYTICS_BASE_URL}/api/v1/insights/summary`, {
    headers: { 'X-Admin-Token': adminToken },
  });
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'Invalid admin token' : 'Failed to load summary');
  }
  return response.json();
}

export async function fetchInsightVisits(adminToken, { limit = 100, offset = 0 } = {}) {
  const response = await fetch(
    `${ANALYTICS_BASE_URL}/api/v1/insights/visits?limit=${limit}&offset=${offset}`,
    { headers: { 'X-Admin-Token': adminToken } }
  );
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'Invalid admin token' : 'Failed to load visits');
  }
  return response.json();
}

export async function fetchInsightConsents(adminToken) {
  const response = await fetch(`${ANALYTICS_BASE_URL}/api/v1/insights/consents?limit=100`, {
    headers: { 'X-Admin-Token': adminToken },
  });
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'Invalid admin token' : 'Failed to load consents');
  }
  return response.json();
}
